/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import * as React from 'react';
import { animated } from 'react-spring';
import ouiBase from '@goldwasserexchange/oui';
import ouiStyle from '@goldwasserexchange/oui-style';
import {
  InputVariantsNames,
  useRadioHtmlId,
  useBaseVariant,
  useDisabledVariant,
  useValidationVariant,
  useHasFocusVariant,
  InputVariantHooksArray,
} from '../../../../../../../../../../OUI/Inputs';
import {
  focusLabelStyleAdditionalProperties,
  noFocusLabelStyleAdditionalProperties,
  errorLabelStyleAdditionalProperties,
  disabledLabelStyleAdditionalProperties,
} from '../../../../../../../shared';
import { defaultRadioListInputListElementLabelContainerStyle } from '../../../../../../../radio/components';
import { useVariantSpring } from '../../../../../../../../utils';

function useMakedefaultSelectInputListElementLabelContainerInStyle(
  lines: ouiStyle.InStyle['lines'],
) {
  const inStyle = React.useMemo(
    (): ouiStyle.InStyle => ({
      ...defaultRadioListInputListElementLabelContainerStyle,
      parentBorderBottomStyle: 'solid',
      parentBorderBottomWidth: 'simple',
      lines,
    }),
    [lines],
  );
  return inStyle;
}

function useMakedefaultSelectInputListElementLabelContainerCssFromLines(
  lines: ouiStyle.InStyle['lines'],
) {
  const inStyle = useMakedefaultSelectInputListElementLabelContainerInStyle(
    lines,
  );
  const css = React.useMemo(() => ouiStyle.makeCss(inStyle), [inStyle]);
  return css;
}

function useMakedefaultSelectInputListElementLabelContainerCss() {
  const lines = ouiBase.List.useListInputElementLines();
  const css = useMakedefaultSelectInputListElementLabelContainerCssFromLines(
    lines,
  );
  return css;
}

function useDefaultSelectInputListElementLabelVariants(): Record<
  InputVariantsNames,
  Partial<Pick<ouiStyle.Style, 'fontWeight' | 'color'>>
> {
  const lines = ouiBase.List.useListInputElementLines();
  const baseLabelInStyle = useMakedefaultSelectInputListElementLabelContainerInStyle(
    lines,
  );
  return React.useMemo(
    () => ({
      opened: {},
      closed: {},
      base: ouiStyle.makeCssAndExtract(baseLabelInStyle, ['color']),
      hasContent: {},
      noContent: {},
      focus: ouiStyle.makeCssAndExtract(
        { ...baseLabelInStyle, ...focusLabelStyleAdditionalProperties },
        ['fontWeight'],
      ),
      noFocus: ouiStyle.makeCssAndExtract(
        { ...baseLabelInStyle, ...noFocusLabelStyleAdditionalProperties },
        ['fontWeight'],
      ),
      untouched: {},
      valid: {},
      error: ouiStyle.makeCssAndExtract(
        { ...baseLabelInStyle, ...errorLabelStyleAdditionalProperties },
        ['color'],
      ),
      disabled: ouiStyle.makeCssAndExtract(
        { ...baseLabelInStyle, ...disabledLabelStyleAdditionalProperties },
        ['color'],
      ),
      enabled: {},
      checked: {},
      unChecked: {},
      last: {},
      notLast: {},
      before: {},
      inside: {},
      after: {},
      noSelection: {},
      notCorrected: {},
      correct: {},
      incorrect: {},
      correctSelected: {},
      correctNotSelected: {},
      incorrectSelected: {},
      incorrectNotSelected: {},
    }),
    [baseLabelInStyle],
  );
}

const defaultSelectInputListElementLabelContainerInputHooksArray: InputVariantHooksArray = [
  useBaseVariant,
  useHasFocusVariant,
  useValidationVariant,
  useDisabledVariant,
];

export function DefaultSelectInputListElementLabelContainer(
  props: React.PropsWithChildren<{}>,
) {
  const { children } = props;
  const htmlFor = useRadioHtmlId();
  const css = useMakedefaultSelectInputListElementLabelContainerCss();
  const labelVariants = useDefaultSelectInputListElementLabelVariants();
  const spring = useVariantSpring(
    labelVariants,
    defaultSelectInputListElementLabelContainerInputHooksArray,
  );
  return (
    <animated.label htmlFor={htmlFor} css={css} style={spring}>
      {children}
    </animated.label>
  );
}
